export type { LegalSection, LegalDocument } from '@/types/legal'

import type { LegalDocument } from '@/types/legal'

export const PRIVACY_POLICY: LegalDocument = {
  title: 'Privacy Policy',
  kicker: 'Your privacy matters',
  description: 'How Quick Build Con collects, uses and protects your personal information.',
  intro:
    'This application and website is owned and operated by One Step Tech Hub Private Limited. We are deeply committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, store, and disclose information when you use our real estate platform — whether through our website or mobile application — where users can list properties, search for properties, and make inquiries.',
  sections: [
    {
      id: 'welcome',
      title: 'Welcome',
      paragraphs: [
        'By using our app or website, you agree to the terms of this Privacy Policy. Please read it carefully before using our services.',
      ],
    },
    {
      id: 'information-we-collect',
      title: 'Information We Collect',
      paragraphs: ['We collect the following types of information when you use our platform:'],
      bullets: [
        'Registration Information: Name, email address, phone number, and password when you create an account.',
        'Property Listing Information: Property details, photos, location, price, and description submitted by sellers/owners.',
        'Inquiry Information: Messages or contact details submitted when a buyer/renter makes an inquiry about a property.',
        'Device Information: Device type, operating system, app version, and unique device identifiers.',
        'Usage Data: How you interact with the app, properties you view, searches you perform.',
      ],
    },
    {
      id: 'how-we-use',
      title: 'How We Use Your Information',
      paragraphs: ['One Step Tech Hub Private Limited uses the collected information for the following purposes:'],
      bullets: [
        'To create and manage your account on our platform.',
        'To allow you to list, browse, and inquire about properties.',
        'To connect property buyers/renters with sellers/owners.',
        'To send you relevant notifications, updates, and property alerts.',
        'To respond to your queries and provide customer support.',
        'To improve our app features and user experience.',
        'To send promotional communications (you may opt out at any time).',
        'To comply with legal obligations.',
      ],
    },
    {
      id: 'app-permissions',
      title: 'App Permissions',
      paragraphs: [
        'Our mobile application may request the following permissions:',
        'You may deny or revoke any permission at any time through your device settings. Denying certain permissions may limit app functionality.',
      ],
      bullets: [
        'Camera / Storage: To upload property photos when creating a listing.',
        'Phone / Contacts: Only if required to facilitate direct calls to property owners.',
        'Notifications: To alert you about new properties, inquiry responses, and updates.',
      ],
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing & Disclosure',
      paragraphs: ['We do not sell your personal information to any third party. Your information may be shared only in the following cases:'],
      bullets: [
        'Between buyers and sellers on the platform to facilitate property inquiries (e.g., sharing contact details when an inquiry is made).',
        'With trusted service providers who assist us in operating our platform (e.g., cloud hosting, analytics), under strict confidentiality agreements.',
        'When required by law, court order, or governmental authority.',
        'To protect the rights, property, or safety of One Step Tech Hub Private Limited, our users, or the public.',
        'With your explicit written consent.',
      ],
    },
    {
      id: 'data-security',
      title: 'Data Security',
      paragraphs: [
        'We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, loss, or misuse. All data is stored on secure servers. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.',
      ],
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      paragraphs: [
        'We retain your personal data for as long as your account is active or as needed to provide our services. You may request deletion of your account and associated data at any time by contacting us. We will process your request within a reasonable timeframe, subject to any legal obligations.',
      ],
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      paragraphs: ['As a user of our platform, you have the right to:'],
      bullets: [
        'Access the personal information we hold about you.',
        'Request correction of inaccurate information.',
        'Request deletion of your account and personal data.',
        'Opt out of promotional communications at any time.',
      ],
    },
    {
      id: 'children',
      title: "Children's Privacy",
      paragraphs: [
        'Our platform is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that a minor has provided us with personal data, we will delete it promptly.',
      ],
    },
    {
      id: 'third-party',
      title: 'Third-Party Links',
      paragraphs: [
        'Our app or website may contain links to third-party websites or services. We are not responsible for the privacy practices of those third parties and encourage you to review their privacy policies separately.',
      ],
    },
    {
      id: 'policy-changes',
      title: 'Change of Policy',
      paragraphs: [
        'We reserve the right to update this Privacy Policy at any time. Changes will be posted on our website and app with the updated effective date. Continued use of our platform after changes constitutes your acceptance of the updated policy. We recommend reviewing this policy periodically.',
      ],
    },
    {
      id: 'agreement',
      title: 'Agreement to the Policy',
      paragraphs: [
        'By registering, listing a property, or making an inquiry on our platform, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.',
      ],
    },
  ],
  contactNote:
    'If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact One Step Tech Hub Private Limited.',
}

export const TERMS_AND_CONDITIONS: LegalDocument = {
  title: 'Terms & Conditions',
  kicker: 'Platform usage terms',
  description: 'Terms and conditions for using Quick Build Con website and mobile application.',
  intro:
    'Your use of Quick Build Con is subject to the following terms and conditions. By using this website or application you agree to accept our Terms & Conditions and Privacy Policy. One Step Tech Hub Pvt Ltd reserves the right to change these terms at any time.',
  sections: [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      paragraphs: [
        'Please read our Terms and Conditions carefully. By using this website of Quick Build Con you are deemed to agree and accept our Terms and Conditions and Privacy Policy.',
        'You need to check periodically for any changes. Using this website after we make any alteration means you agree to accept the changes. Do not use this website if you choose not to accept and abide by these Terms & Conditions at any time.',
      ],
    },
    {
      id: 'service-terms',
      title: 'Service Terms',
      paragraphs: [
        'OSTH provides products, construction solutions, software and manpower services (collectively referred to as “Services”) subject to the customer’s acceptance of and compliance with these Terms.',
        'The Terms will commence on the date the client enrolled for our Services and will end when terminated by either party in accordance with the Terms.',
        'OSTH has the authority to use its identifying mark in the form of logo, design, URL or brand identity on websites produced, without separate consent of customers or clients.',
      ],
    },
    {
      id: 'description',
      title: 'Description of Service',
      paragraphs: [
        'OSTH may provide one or more services including, but not limited to: construction solution provider, website design & development, internet marketing, mobile application development, content development, and/or maintenance and support services.',
        'Unless explicitly stated otherwise, any new feature that augments or enhances the Services shall be considered part of the Services. OSTH reserves the right to modify, suspend or discontinue the Services at any time, without notice, based on non-cooperation, non-payment, or unwanted delay from the client.',
      ],
    },
    {
      id: 'website-changes',
      title: 'Changes to Website',
      paragraphs: [
        'OSTH has the sole right to change or remove the website (temporarily or permanently) or any part of it at any time, without notice. OSTH shall not be liable to anyone for any such changes or removal.',
      ],
    },
    {
      id: 'platform-listings',
      title: 'Property Listings & Platform Use',
      paragraphs: [
        'Property listings must be accurate, lawful and submitted by the rightful owner or authorised agent. QBC reserves the right to remove fraudulent, misleading or duplicate listings without prior notice.',
        'QBC acts as a technology platform connecting buyers and sellers. We are not a party to property transactions and do not guarantee sale, rent or lease outcomes.',
        'Users are responsible for verifying property details, documentation and RERA compliance before entering into any transaction.',
      ],
    },
    {
      id: 'database-development',
      title: 'Database, E-Commerce & Application Development',
      paragraphs: [
        'OSTH is not liable for any losses caused by any software that is created for the client. The client is responsible to ensure that all products and software are functioning properly before use.',
        'Where applications are developed on servers not provided by OSTH, the client is responsible for providing information, support, additional software and cooperation required for correct development.',
        'The client is expected to fully test applications before making them generally available. OSTH will endeavour to correct errors found after go-live to meet the standards outlined in the brief.',
      ],
    },
    {
      id: 'website-design',
      title: 'Website Design',
      paragraphs: [
        'We make every effort to ensure design and development work is error free; however OSTH does not accept responsibility for losses incurred because of website malfunction.',
        'OSTH will be the rightful owner of the web server, website, graphics, content, and programming code until the client pays all outstanding accounts in full.',
        'OSTH will not be liable for copyright infringements caused by materials submitted by the client, or for loss of earnings due to unavailability of the website or related services.',
      ],
    },
    {
      id: 'copyright',
      title: 'Copyright',
      paragraphs: [
        'OSTH owns or has license to use the trademarks, copyright and intellectual property rights of the site and its content including website design, graphics, text, source codes and all software connected with the website.',
        'You may access content only for personal and non-commercial home use. You cannot download, copy, transmit, reproduce, store, distribute or sell the content without prior written consent of OSTH.',
      ],
    },
    {
      id: 'disclaimer',
      title: 'Disclaimers & Limitation of Liability',
      paragraphs: [
        'The website of OSTH is provided on an “AS AVAILABLE” and “AS IS” basis. OSTH is not responsible for any direct, indirect or consequential damage or loss (including loss of business, data, opportunity and/or profit) caused due to use of the website.',
        'OSTH does not warrant that website functionality will be error free or uninterrupted, or that the website or server are free of viruses or harmful components.',
      ],
    },
    {
      id: 'cancellation',
      title: 'Cancellation & Refund Policy',
      paragraphs: [
        'All amounts owed for services rendered prior to the verified cancellation date must be paid in full. Cancellation requests must be made in writing or via email by the initial authorizing party.',
        'There will be no refunds for cancellation requests made after the cooling-off period of 7 days from the date of order, unless otherwise mutually agreed.',
        'Premium marketing packages on QBC may be non-refundable once activation has begun, unless stated otherwise at the time of purchase.',
      ],
    },
    {
      id: 'payment',
      title: 'Payment',
      paragraphs: [
        'Client agrees to pay OSTH the service fee pursuant to the payment plan selected, including applicable taxes, in accordance with billing terms in effect when the fee becomes payable.',
        'OSTH reserves the right to pursue alternative means of payment up to and including debt collection services. The customer shall be liable for all collection costs including attorneys’ fees where applicable.',
      ],
    },
    {
      id: 'payment-collection',
      title: 'Payment Collection',
      paragraphs: [
        'The client is responsible to maintain an active and valid payment method on file at all times. If payment is not available, OSTH may temporarily turn off websites, ads, sponsored listings or ongoing services.',
        'If payment is not received, OSTH reserves the right to terminate the agreement and retain ownership of the website or services until the account is paid in full.',
      ],
    },
    {
      id: 'representations',
      title: 'Representations & Warranties',
      paragraphs: [
        'Client represents that they have sufficient authority to enter into the agreement, are using services for lawful commercial purposes, and have the necessary rights to provide all information for use as described.',
      ],
    },
    {
      id: 'agreement',
      title: 'Agreement to the Policy',
      paragraphs: [
        'By submitting personal details to our website or app, you indicate acceptance of these terms. For queries or concerns, please contact us using the details below.',
      ],
    },
  ],
  contactNote: 'For questions about these Terms & Conditions, contact One Step Tech Hub Private Limited.',
}
